/**
 * Line icons of the Admin Ultimate Design (어드민 궁극의 디자인, 2026-10-17): one stroke weight, drawn in the
 * text colour, so a button reads the same on every desk screen. They are decoration only; the button's own
 * words carry its name, so every icon is hidden from assistive technology.
 */
import type { ReactElement, SVGProps } from 'react';

type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'>;

const base = (paths: ReactElement, { className, ...props }: IconProps): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    className={['ff-icon', className].filter(Boolean).join(' ')}
    aria-hidden="true"
    focusable="false"
    {...props}
  >
    {paths}
  </svg>
);

export const SearchIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
    </>,
    props,
  );

export const ScanIcon = (props: IconProps) =>
  base(<path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M7 7v10m3-10v10m4-10v10m3-10v10M2 12h20" />, props);

export const PrintIcon = (props: IconProps) =>
  base(<path d="M6 9V3h12v6M6 18H3V9h18v9h-3M6 14h12v7H6zM17 11h1" />, props);

export const CommentIcon = (props: IconProps) =>
  base(
    <>
      <path d="M4 4h16v12H9l-5 5z" />
      <path d="M8 8h8m-8 4h5" />
    </>,
    props,
  );

export const GearIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </>,
    props,
  );

export const WarningIcon = (props: IconProps) =>
  base(
    <>
      <path d="M12 3 2 21h20z" />
      <path d="M12 10v5m0 3h.01" />
    </>,
    props,
  );

export const RefreshIcon = (props: IconProps) =>
  base(
    <>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" />
      <path d="M20 4v5h-5" />
    </>,
    props,
  );

export const DownloadIcon = (props: IconProps) => base(<path d="M12 3v12m-5-5 5 5 5-5M4 17v3h16v-3" />, props);

export const UploadIcon = (props: IconProps) => base(<path d="M12 15V3m-5 5 5-5 5 5M4 17v3h16v-3" />, props);

export const PencilIcon = (props: IconProps) =>
  base(
    <>
      <path d="M4 20h4L19 9l-4-4L4 16z" />
      <path d="m13.5 6.5 4 4" />
    </>,
    props,
  );

/** Closes a card: the mark on a notification's dismiss button instead of the ✕ glyph (SSD-7705). */
export const CloseIcon = (props: IconProps) => base(<path d="M6 6l12 12M18 6 6 18" />, props);

/** Something done: the mark of a success notification (SSD-7705). */
export const CheckIcon = (props: IconProps) => base(<path d="m5 12.5 4.5 4.5L19 7" />, props);

/** Something the operator expected did not happen: the mark of an error notification (SSD-7705). */
export const ErrorIcon = (props: IconProps) =>
  base(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V13m0 3.5h.01" />
    </>,
    props,
  );
